<script setup lang="ts">
import { computed, onBeforeMount, ref } from "vue";
import Table from "../../../base-components/Table";
import Preview from "../../../base-components/Preview";
import { FormInput } from "../../../base-components/Form";
import Button from "../../../base-components/Button";
import LoadingIcon from "../../../base-components/LoadingIcon";
import Lucide from "../../../base-components/Lucide/Lucide.vue";
import { Menu } from "../../../base-components/Headless";
import { useUsuariosStore } from "../../../services/usuarios";
import ModalUsuariosVer from "./modals/ModalUsuariosVer.vue";
import ModalUsuariosEditar from "./modals/ModalUsuariosEditar.vue";
import ModalUsuariosEstablecerContrasena from "./modals/ModalUsuariosEstablecerContrasena.vue";
import ModalUsuariosEstablecerPin from "./modals/ModalUsuariosEstablecerPin.vue";
import { useUsuarioRolesStore } from "../../../services/usuarioRoles";
import { userUserStore } from "../../../stores/user";
import { isAdmin } from "../../../utils/permisos";
import {
  oAuthGoogleSolicitarAcceso,
  Usuario
} from "@fixeduy/restaurantestypes";
import { oAuthGoogleGenerarURLLectorMails } from "../../../services/cloudFunctions/oAuthGoogleGenerarURLLectorMails";

const usuariosStore = useUsuariosStore();
const usuariosRolesStore = useUsuarioRolesStore();
const userStore = userUserStore();

onBeforeMount(() => {
  if (usuariosRolesStore.roles.length == 0) {
    usuariosRolesStore.getRolesFromFirestore();
  }
  usuariosStore.getFromFirestore();
});

const cargandoURLPermisos = ref(false);

const mailFixedAutomatico = computed(() => {
  return (
    userStore.user != null &&
    userStore.user.email != null &&
    (userStore.user.email.endsWith("@fixed.uy") ||
      userStore.user.email.endsWith("@atiendo.cloud"))
  );
});

const handlerPermisosOAuthLectorMailFixed = async () => {
  cargandoURLPermisos.value = true;
  const params: oAuthGoogleSolicitarAcceso = {
    redirectURL: window.location.origin
  };
  const urlRedirect = await oAuthGoogleGenerarURLLectorMails(params);
  if (urlRedirect.exito) {
    const url = urlRedirect.errorMsg;
    window.open(url);
  }
  cargandoURLPermisos.value = false;
};

const inputBuscar = ref("");
const mostrarUsuario = (usuario: Usuario) => {
  const buscar = inputBuscar.value.toLocaleLowerCase();
  return (
    buscar == "" ||
    usuario.nombre.toLocaleLowerCase().search(buscar) != -1 ||
    usuario.mail.toLocaleLowerCase().search(buscar) != -1
  );
};

enum showModalType {
  none,
  Ver,
  Editar,
  EstablecerContrasena,
  EstablecerPin
}

const modal = ref<showModalType>(showModalType.none);
const usuarioSeleccionado = ref<Usuario | null>(null);

const showModal = (usuario: Usuario, modalShow: showModalType) => {
  modal.value = modalShow;
  usuarioSeleccionado.value = usuario;
};

const cerrarModal = () => {
  modal.value = showModalType.none;
  usuarioSeleccionado.value = null;
};

const mostrarUsuarioMail = (mail: string) => {
  if (mail.includes(".fixed.uy")) {
    const mailSplit = mail.split("@");
    return mailSplit[0] + " (acceso con RUT y usuario)";
  } else {
    return mail;
  }
};

const nombreRol = (rolId: string) => {
  const rol = usuariosRolesStore.getRolById(rolId);
  return rol?.rol ?? "—";
};

const usuariosVisibles = computed(() =>
  usuariosStore.usuarios.filter(u => mostrarUsuario(u))
);
const mostrarEstadoVacio = computed(
  () =>
    !usuariosStore.cargando &&
    usuariosStore.usuarios.length > 0 &&
    usuariosVisibles.value.length === 0
);
const mostrarSinDatos = computed(
  () => !usuariosStore.cargando && usuariosVisibles.value.length === 0
);
</script>

<template>
  <div></div>
  <Preview class="mt-5 box">
    <div class="grid grid-cols-3 gap-4">
      <div class="col-span-2 p-5">
        <h2 class="text-base font-medium">Usuarios</h2>
        <FormInput
          class="mt-2"
          type="text"
          placeholder="Buscar..."
          v-model="inputBuscar"
        />
      </div>
    </div>
    <div class="px-5 min-w-0">
      <Preview.Panel>
        <Button
          v-if="!cargandoURLPermisos && mailFixedAutomatico"
          variant="danger"
          class="mb-2 mr-10 mt-5"
          @click="handlerPermisosOAuthLectorMailFixed"
        >
          Brindar permisos - Lector mails Fixed (solo visible @fixed.uy y
          @atiendo.uy)
        </Button>
        <div class="overflow-x-auto min-w-0 -mx-5 px-5">
          <Table hover class="min-w-[600px]">
            <Table.Thead>
              <Table.Tr>
                <Table.Th class="whitespace-nowrap"> Nombre</Table.Th>
                <Table.Th class="whitespace-nowrap"> Mail / Usuario</Table.Th>
                <Table.Th class="whitespace-nowrap"> Rol</Table.Th>
                <Table.Th class="whitespace-nowrap"> Creado el </Table.Th>

                <Table.Th class="whitespace-nowrap"> </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <template
                v-for="usuario in usuariosStore.usuarios"
                :key="usuario.id"
              >
                <Table.Tr v-if="mostrarUsuario(usuario)">
                  <Table.Td
                    >{{ usuario.nombre }}
                    <template v-if="!usuario.activo">
                      <span class="bloqueado"> Bloqueado </span>
                    </template>
                  </Table.Td>
                  <Table.Td>{{ mostrarUsuarioMail(usuario.mail) }}</Table.Td>
                  <Table.Td>{{ nombreRol(usuario.rolId) }}</Table.Td>
                  <Table.Td>{{ usuario.creadoElString }}</Table.Td>

                  <Table.Td>
                    <div class="flex justify-end">
                      <Menu>
                        <Menu.Button :as="Button" class="px-2 !box">
                          <span class="flex items-right justify-center w-5 h-5">
                            <Lucide icon="Plus" class="w-4 h-4" />
                          </span>
                        </Menu.Button>
                        <Menu.Items class="w-40" placement="left-end">
                          <Menu.Item
                            @click="showModal(usuario, showModalType.Ver)"
                          >
                            <Lucide icon="Eye" class="w-4 h-4 mr-2" />
                            Ver datos completos
                          </Menu.Item>
                          <Menu.Item
                            @click="showModal(usuario, showModalType.Editar)"
                          >
                            <Lucide icon="Edit2" class="w-4 h-4 mr-2" />
                            Editar
                          </Menu.Item>
                          <Menu.Item
                            v-if="
                              (isAdmin && usuario.mail.endsWith('.fixed.uy')) ||
                              userStore.user?.email?.endsWith('@fixed.uy')
                            "
                            @click="
                              showModal(
                                usuario,
                                showModalType.EstablecerContrasena
                              )
                            "
                          >
                            <Lucide
                              icon="Edit2"
                              class="w-4 h-4 mr-2 text-danger"
                            />
                            Establecer contraseña
                          </Menu.Item>
                          <Menu.Item
                            @click="
                              showModal(usuario, showModalType.EstablecerPin)
                            "
                          >
                            <Lucide icon="KeyRound" class="w-4 h-4 mr-2" />
                            Establecer PIN
                          </Menu.Item>
                        </Menu.Items>
                      </Menu>
                    </div>
                  </Table.Td>
                </Table.Tr>
              </template>
              <Table.Tr v-if="usuariosStore.cargando">
                <Table.Td colspan="30">
                  <div class="w-8 mx-auto">
                    <LoadingIcon icon="oval" class="w-8 h-8" />
                  </div>
                  <div class="w-auto mx-auto text-center mt-2">Cargando...</div>
                </Table.Td>
              </Table.Tr>
              <Table.Tr v-else-if="mostrarSinDatos">
                <Table.Td colspan="30" class="text-center py-8 text-slate-500">
                  {{
                    mostrarEstadoVacio
                      ? "Ningún usuario coincide con la búsqueda."
                      : "No hay usuarios para mostrar."
                  }}
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </div>
      </Preview.Panel>
    </div>
    <ModalUsuariosVer
      v-if="modal == showModalType.Ver && usuarioSeleccionado != null"
      :usuario="usuarioSeleccionado!"
      @cerrar="cerrarModal"
    />
    <ModalUsuariosEditar
      v-if="modal == showModalType.Editar && usuarioSeleccionado != null"
      :usuario="usuarioSeleccionado!"
      @cerrar="cerrarModal"
    />
    <ModalUsuariosEstablecerContrasena
      v-if="
        modal == showModalType.EstablecerContrasena &&
        usuarioSeleccionado != null
      "
      :usuario="usuarioSeleccionado!"
      @cerrar="cerrarModal"
    />
    <ModalUsuariosEstablecerPin
      v-if="modal == showModalType.EstablecerPin && usuarioSeleccionado != null"
      :usuario="usuarioSeleccionado!"
      @cerrar="cerrarModal"
    />
  </Preview>
</template>

<style scoped>
.bloqueado {
  color: rgb(185 28 28);
  padding: 0 0 0 10px;
}
</style>
